/**
 * View dock (bottom left): the basemap style and the planning layer, each opening a visual
 * picker ("tray") with real-render previews of every option. Hovering / focusing a card shows a
 * larger preview with a one-line description; choosing applies it immediately.
 * Thumbnails: public/assets/img/thumbs (generated from the app by scripts/make-thumbs.mjs).
 */
import { $, $$, esc, icon, isMobile, hideAnimated, scrollHints, scrollWithin } from './ui.js';
import { bus, prefs, theme } from './state.js';
import { BASEMAPS, BASEMAP_BY_ID, PLAN_LAYERS, PLAN_BY_NO, planFrom } from './config.js';
import * as layers from './layers.js';

const thumb = (id, small = true) => `/assets/img/thumbs/${id}${small ? '-s' : ''}.webp`;
const planThumb = (n) => (n ? `pl-${n}` : 'bm-positron');

/** One line per basemap: what it is good for. */
const BM_INFO = {
  positron: { desc: 'Nền xám nhạt, ít chi tiết: màu quy hoạch nổi rõ nhất.', tag: 'Đọc quy hoạch' },
  liberty: { desc: 'Đầy đủ tên đường, công trình và điểm dịch vụ.', tag: 'Tìm đường, địa điểm' },
  bright: { desc: 'Màu sắc rõ, phân biệt cây xanh, mặt nước, khu dân cư.', tag: 'Tổng quan đô thị' },
  dark: { desc: 'Dịu mắt khi xem buổi tối, lớp quy hoạch giữ nguyên màu.', tag: 'Ban đêm' },
  imagery: { desc: 'Ảnh vệ tinh thực tế để đối chiếu hiện trạng với quy hoạch.', tag: 'Hiện trạng' },
  hybrid: { desc: 'Ảnh vệ tinh kèm tên đường và địa danh.', tag: 'Hiện trạng + tên đường' },
};
const PLAN_INFO = {
  654: 'Chức năng từng ô đất, chỉ tiêu mật độ, tầng cao theo đồ án phân khu.',
  gt: 'Mạng lưới đường quy hoạch, metro, đường sắt; nhấp đường để xem lộ giới ước tính.',
  673: 'Các đồ án chi tiết 1/500 đã số hoá, đến từng lô đất.',
  666: 'Sử dụng đất theo quy hoạch chung Thủ đô cho toàn thành phố.',
  667: 'Các vùng chức năng lớn: nội đô lịch sử, đô thị vệ tinh, vùng sinh thái.',
  895: 'Định hướng sử dụng đất của quy hoạch tổng thể Thủ đô tầm nhìn 100 năm.',
  sdd: 'Bản đồ kế hoạch sử dụng đất hằng năm của 30 quận, huyện (2018–2024) và QHSDĐ đến 2020 · Sở Nông nghiệp và Môi trường.',
};

let pm = null;
let mode = null; // 'basemap' | 'plan' | null
const els = {};

export function initDock(planMap) {
  pm = planMap;
  els.dock = $('#dock');
  els.bm = $('#dock-bm');
  els.chip = $('#layer-chip');
  els.tray = $('#tray');
  els.bm.addEventListener('click', () => toggleTray('basemap'));
  els.chip.addEventListener('click', () => toggleTray('plan'));
  els.tray.addEventListener('click', onClick);
  els.tray.addEventListener('pointerover', onHover);
  els.tray.addEventListener('focusin', onHover);
  els.tray.addEventListener('pointerleave', () => peek(null));
  els.tray.addEventListener('keydown', onKey);
  document.addEventListener('pointerdown', (e) => {
    if (mode && !els.tray.contains(e.target) && !els.dock.contains(e.target)) closeTray();
  }, true);
  bus.on('basemap', () => { syncDock(); if (mode === 'basemap') render(); });
  bus.on('plan', () => { syncDock(); if (mode === 'plan') render(); });
  bus.on('theme', () => syncDock());
  bus.on('drawer', (open) => { if (open) closeTray(); });
  bus.on('tray', (m) => openTray(m));
  syncDock();
}

export const isTrayOpen = () => !!mode;

export function openTray(m) {
  if (els.tray.classList.contains('closing')) els.tray.classList.remove('closing');
  mode = m;
  if (layers.isDrawerOpen() && isMobile()) layers.closeDrawer();
  render();
  els.tray.hidden = false;
  els.bm.setAttribute('aria-expanded', String(m === 'basemap'));
  els.chip.setAttribute('aria-expanded', String(m === 'plan'));
  requestAnimationFrame(() => { const c = $('.tcard[aria-checked=true]', els.tray); c?.focus({ preventScroll: true }); scrollWithin($('.tray-body', els.tray), c, { margin: 8 }); });
}

export async function closeTray() {
  if (!mode) return;
  mode = null;
  els.bm.setAttribute('aria-expanded', 'false');
  els.chip.setAttribute('aria-expanded', 'false');
  await hideAnimated(els.tray, 'closing', 180);
}

export function toggleTray(m) {
  if (mode === m) closeTray();
  else openTray(m);
}

/* ---------------- dock buttons ---------------- */

function syncDock() {
  const bm = BASEMAP_BY_ID[pm.basemap] || BASEMAPS[0];
  $('#dock-bm-img').src = thumb('bm-' + bm.id);
  $('#dock-bm-name').textContent = bm.title;
  els.bm.setAttribute('aria-label', `Bản đồ nền: ${bm.title}. Đổi kiểu bản đồ`);
  const p = prefs.plan;
  $('#layer-chip-img').src = thumb(planThumb(p));
  els.chip.classList.toggle('none', !p);
}

/* ---------------- tray ---------------- */

function items() {
  if (mode === 'basemap') {
    const cur = pm.basemap;
    return BASEMAPS.map((b) => ({ key: b.id, title: b.title, img: 'bm-' + b.id, icon: b.icon, on: b.id === cur, desc: BM_INFO[b.id]?.desc || '', tag: BM_INFO[b.id]?.tag || '' }));
  }
  return PLAN_LAYERS.map((l) => ({ key: String(l.layerNo), title: l.title, img: planThumb(l.layerNo), icon: l.icon, on: prefs.plan === l.layerNo, desc: PLAN_INFO[l.layerNo] || l.sub, tag: l.short }));
}

function render() {
  const list = items();
  const sel = list.find((it) => it.on) || null;
  const isBm = mode === 'basemap';
  const followTheme = !prefs.basemap;
  els.tray.dataset.mode = mode;
  els.tray.innerHTML = `
    <header class="tray-head">
      <div><span class="eyebrow">${icon(isBm ? 'layers-2' : 'layers')}${isBm ? 'Kiểu xem bản đồ' : 'Lớp dữ liệu'}</span><h2 id="tray-title">${isBm ? 'Bản đồ nền' : 'Lớp quy hoạch'}</h2></div>
      <button type="button" class="icon-btn ghost" data-act="close" aria-label="Đóng">${icon('x')}</button>
    </header>
    <div class="tray-body">
    <div class="tray-peek" id="tray-peek" aria-live="polite">${peekHtml(sel || list[0], !sel)}</div>
    <div class="tray-grid" role="radiogroup" aria-labelledby="tray-title">
      ${list.map((it) => `
        <button type="button" class="tcard" role="radio" aria-checked="${it.on}" tabindex="${it.on || (!sel && it === list[0]) ? 0 : -1}" data-key="${esc(it.key)}">
          <span class="tcard-img"><img src="${thumb(it.img)}" alt="" width="240" height="150" loading="lazy" decoding="async"><span class="tcard-check">${icon('check')}</span></span>
          <span class="tcard-title">${esc(it.title)}</span>
        </button>`).join('')}
    </div>
    </div>
    <footer class="tray-foot">
      ${isBm
        ? `<button type="button" class="chip-toggle" data-act="follow" aria-pressed="${followTheme}">${icon(followTheme ? 'circle-check' : 'circle')}Tự đổi theo giao diện ${theme() === 'dark' ? 'tối' : 'sáng'}</button>`
        : `<button type="button" class="chip-toggle" data-act="none" aria-pressed="${!prefs.plan}">${icon(prefs.plan ? 'eye-off' : 'circle-check')}${prefs.plan ? 'Ẩn lớp quy hoạch' : 'Đang ẩn lớp quy hoạch'}</button>
           <button type="button" class="btn sm subtle" data-act="legend">${icon('list')}Chú giải &amp; tuỳ chọn</button>`}
    </footer>`;
  els.tray.classList.remove('scrolled', 'more-below');
  scrollHints($('.tray-body', els.tray), els.tray);
  // warm the large preview of the current choice
  if (sel) { const im = new Image(); im.src = thumb(sel.img, false); }
}

function peekHtml(it, hint = false) {
  if (!it) return '';
  return `
    <span class="peek-img"><img src="${thumb(it.img, false)}" alt="Xem trước: ${esc(it.title)}" width="640" height="400" decoding="async"></span>
    <span class="peek-text">
      <span class="peek-kicker">${hint ? 'Xem trước' : it.on ? `${icon('check', 'xs')}Đang dùng` : 'Xem trước'}</span>
      <strong>${esc(it.title)}</strong>
      <span class="peek-desc">${esc(it.desc)}</span>
      ${it.tag && mode === 'basemap' ? `<span class="badge accent">${esc(it.tag)}</span>` : ''}
    </span>`;
}

let peekKey = null;
function peek(key) {
  const list = items();
  const it = (key && list.find((x) => x.key === key)) || list.find((x) => x.on) || list[0];
  if (!it || it.key === peekKey) return;
  peekKey = it.key;
  const host = $('#tray-peek', els.tray);
  if (!host) return;
  host.innerHTML = peekHtml(it);
  host.classList.remove('swap');
  void host.offsetWidth; // restart the cross-fade
  host.classList.add('swap');
}

function onHover(e) {
  const c = e.target.closest?.('.tcard');
  if (c) peek(c.dataset.key);
}

function choose(key) {
  if (mode === 'basemap') layers.setBasemap(key);
  else layers.setPlan(planFrom(key) ?? null, { announce: false });
  peekKey = null;
  render();
  $(`.tcard[data-key="${key}"]`, els.tray)?.focus({ preventScroll: true });
  if (isMobile()) setTimeout(closeTray, 260);
}

function onClick(e) {
  const t = e.target.closest('[data-key],[data-act]');
  if (!t) return;
  if (t.dataset.key) { choose(t.dataset.key); return; }
  switch (t.dataset.act) {
    case 'close': closeTray(); break;
    case 'follow': layers.setBasemap(theme() === 'dark' ? 'dark' : 'positron'); break;
    case 'none': layers.setPlan(prefs.plan ? null : 654); break;
    case 'legend': closeTray(); layers.openDrawer({ legend: true }); break;
    default:
  }
}

/** Arrow keys move within the grid (3 columns), Enter / Space choose. */
function onKey(e) {
  if (e.key === 'Escape') { e.stopPropagation(); const back = mode === 'plan' ? els.chip : els.bm; closeTray(); back.focus(); return; }
  const c = e.target.closest('.tcard');
  if (!c) return;
  const cards = $$('.tcard', els.tray);
  const i = cards.indexOf(c);
  const cols = getComputedStyle($('.tray-grid', els.tray)).gridTemplateColumns.split(' ').length || 3;
  const d = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols }[e.key];
  if (d === undefined) return;
  e.preventDefault();
  const n = cards[(i + d + cards.length) % cards.length];
  cards.forEach((x) => x.setAttribute('tabindex', x === n ? 0 : -1));
  n.focus();
}
